import React from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import logo from '../../../assets/images/logo/SprintRayLogo-Original.png';

// Branded in-app header — same structure/purpose as sre000-order-plugin's
// SprintRayOrderForm Header (logo + title/subtitle column + contact
// column), reusing the same SprintRay logo asset for visual consistency
// across SprintRay tools. Rendered unconditionally as part of the
// calculator itself (not toggled by embed context) — this is how the
// order form does it too: the surrounding page separately decides whether
// to hide the *theme's* header/footer (see the "Header Footer Layout" ACF
// fields), while the tool's own header is always part of the component.
// Reference tool's FR/EN/ES/IT/DE header toggle — but this is WordPress
// *multisite*, not a client-side i18n switch: each language is a whole
// separate site, so a "language link" is a full page navigation to that
// site's own copy of this page (see Settings.php `build_language_links()`).
// Plain <a> tags, not client-side routing.

// The reference tool's own switcher uses flag-icons (github.com/lipis/flag-
// icons) keyed by ISO 3166-1 *country* code, not language code — confirmed
// live: its English option renders a UK flag ("fi-gb"), not a US one, which
// is exactly why this can't just reuse the language subtag directly (our
// own English site's locale is en-US, whose country half actually is "us").
// Only mapped for the languages this multisite network actually has sites
// for; anything else falls back to treating the language code as the
// country code, which happens to already be correct for e.g. fr/es.
const FLAG_COUNTRY_BY_LANGUAGE = {
	en: 'gb',
};

const flagCountryCode = ( languageCode ) =>
	FLAG_COUNTRY_BY_LANGUAGE[ languageCode ] ?? languageCode;

const flagClassName = ( code ) =>
	`roi-calculator__flag roi-calculator__flag--${ flagCountryCode(
		code.split( '-' )[ 0 ]
	) }`;

// Two modes (the calculator's `language_switcher` field, Languages.php):
// `languages` set = switch right here, no navigation (buttons); otherwise
// `languageLinks` to the sibling language sites.
export const LanguageSwitcher = ( {
	languageLinks,
	languages,
	currentLanguage,
	onSelectLanguage,
	disabled = false,
} ) => {
	if ( languages?.length > 1 ) {
		return (
			<nav
				className="roi-calculator__header-langs"
				aria-label={ __( 'Language', 'sprintray-roi-calculator' ) }
			>
				{ languages.map( ( language ) => {
					const isCurrent = language.code === currentLanguage;

					return (
						<button
							key={ language.code }
							type="button"
							className={ `roi-calculator__header-lang${
								isCurrent ? ' is-current' : ''
							}` }
							aria-pressed={ isCurrent }
							title={ language.label }
							lang={ language.code }
							disabled={ disabled }
							onClick={ () =>
								! isCurrent && onSelectLanguage( language )
							}
						>
							<span
								className={ flagClassName( language.code ) }
								aria-hidden="true"
							/>
							<span className="roi-calculator__visually-hidden">
								{ language.label }
							</span>
						</button>
					);
				} ) }
			</nav>
		);
	}

	if ( ! languageLinks || languageLinks.length < 2 ) {
		return null;
	}

	return (
		<nav
			className="roi-calculator__header-langs"
			aria-label={ __( 'Language', 'sprintray-roi-calculator' ) }
		>
			{ languageLinks.map( ( link ) => {
				return link.isCurrent ? (
					<span
						key={ link.code }
						className="roi-calculator__header-lang is-current"
						aria-current="true"
						title={ link.label }
					>
						<span
							className={ flagClassName( link.code ) }
							aria-hidden="true"
						/>
						<span className="roi-calculator__visually-hidden">
							{ link.label }
						</span>
					</span>
				) : (
					<a
						key={ link.code }
						href={ link.url }
						className="roi-calculator__header-lang"
						title={ link.label }
					>
						<span
							className={ flagClassName( link.code ) }
							aria-hidden="true"
						/>
						<span className="roi-calculator__visually-hidden">
							{ link.label }
						</span>
					</a>
				);
			} ) }
		</nav>
	);
};

LanguageSwitcher.propTypes = {
	languageLinks: PropTypes.arrayOf(
		PropTypes.shape( {
			code: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			url: PropTypes.string.isRequired,
			isCurrent: PropTypes.bool.isRequired,
		} )
	),
	languages: PropTypes.arrayOf(
		PropTypes.shape( {
			code: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		} )
	),
	currentLanguage: PropTypes.string,
	onSelectLanguage: PropTypes.func,
	disabled: PropTypes.bool,
};

// Branding only — logo + title/subtitle. Sits in the calculator's left
// column, beside the language switcher/info sidebar (see component.js's
// `__top` row), rather than spanning the full width as its own bar: the
// reference tool's (simuprint.vercel.app) own logo/title sits directly
// above the intro text too, not in a separate full-bleed header, which is
// what let its layout stay compact instead of leaving the intro text and
// the sidebar cards fighting over unrelated amounts of vertical space.
const Header = ( { calculatorType } ) => (
	<header className="roi-calculator__header-branding">
		<img
			src={ logo }
			alt={ __( 'SprintRay Logo', 'sprintray-roi-calculator' ) }
			className="roi-calculator__header-logo"
		/>
		<div className="roi-calculator__header-titles">
			<h1 className="roi-calculator__header-title">
				{ __( 'ROI Calculator', 'sprintray-roi-calculator' ) }
			</h1>
			<h2 className="roi-calculator__header-subtitle">
				{ calculatorType === 'labor'
					? __( 'Laboratory', 'sprintray-roi-calculator' )
					: __( 'Dental Practice', 'sprintray-roi-calculator' ) }
			</h2>
		</div>
	</header>
);

Header.propTypes = {
	calculatorType: PropTypes.string,
};

export default Header;
